import React, { useState } from 'react';
import { Zap, ExternalLink, Check, Sparkles, Wand2 } from 'lucide-react';
import Button from './Button';
import { ImageSize, ImageModel } from '../types';
import { generateConceptImage, enhancePrompt } from '../services/geminiService';

interface GeneratorProps {
  onSave: (url: string, prompt: string) => void;
}

const Generator: React.FC<GeneratorProps> = ({ onSave }) => {
  const [prompt, setPrompt] = useState('');
  const [size, setSize] = useState<ImageSize>(ImageSize.SIZE_1K);
  const [model, setModel] = useState<ImageModel>(ImageModel.NANO_BANANA_PRO);
  const [result, setResult] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isEnhancing, setIsEnhancing] = useState(false);

  const handleGenerate = async () => {
    if (!prompt.trim()) return;

    setIsGenerating(true);
    try {
      const resultBase64 = await generateConceptImage(prompt, size, model);
      setResult(resultBase64);
    } catch (error) {
      console.error(error);
      alert("Generation failed. Please ensure your VITE_API_KEY is correctly set in Vercel.");
    } finally {
      setIsGenerating(false);
    }
  };

  const handleEnhance = async () => {
    if (!prompt.trim()) return;
    setIsEnhancing(true);
    try {
      const enhanced = await enhancePrompt(prompt);
      setPrompt(enhanced);
    } catch (error) {
      console.error(error);
    } finally {
      setIsEnhancing(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto pb-12">
      <div className="mb-10 flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <div className="flex items-center gap-3 mb-3">
            <h2 className="text-4xl font-serif font-bold text-stone-900">Concept Lab</h2>
          </div>
          <p className="text-lg text-stone-500 font-light max-w-2xl">Generate high-resolution jewelry concepts from scratch.</p>
        </div>
        
        {/* Model Selector */}
        <div className="flex items-center bg-white border border-stone-200 rounded-xl p-1 shadow-sm">
           <button 
             onClick={() => setModel(ImageModel.NANO_BANANA)}
             className={`px-4 py-2 rounded-lg text-xs font-bold uppercase tracking-wider transition-all ${model === ImageModel.NANO_BANANA ? 'bg-stone-900 text-white shadow-md' : 'text-stone-500 hover:text-stone-900 hover:bg-stone-50'}`}
           >
             Nano Banana
           </button>
           <button 
             onClick={() => setModel(ImageModel.NANO_BANANA_PRO)}
             className={`px-4 py-2 rounded-lg text-xs font-bold uppercase tracking-wider transition-all flex items-center ${model === ImageModel.NANO_BANANA_PRO ? 'bg-stone-900 text-white shadow-md' : 'text-stone-500 hover:text-stone-900 hover:bg-stone-50'}`}
           >
             Nano Banana Pro
             <Zap className={`w-3 h-3 ml-1.5 ${model === ImageModel.NANO_BANANA_PRO ? 'text-amber-400 fill-amber-400' : 'text-stone-400'}`} />
           </button>
        </div>
      </div>

      <div className="grid lg:grid-cols-12 gap-10 items-start">
        <div className="lg:col-span-4 space-y-6">
          <div className="bg-white p-8 rounded-3xl border border-stone-100 shadow-xl shadow-stone-200/50">
            <div className="flex justify-between items-center mb-3">
               <label className="text-xs font-bold text-stone-400 uppercase tracking-wider block">Concept Description</label>
               <button 
                  onClick={handleEnhance} 
                  disabled={!prompt.trim() || isEnhancing}
                  className="text-xs flex items-center text-purple-600 hover:text-purple-700 font-medium disabled:opacity-50"
               >
                  <Wand2 className={`w-3 h-3 mr-1 ${isEnhancing ? 'animate-spin' : ''}`} />
                  {isEnhancing ? 'Enhancing...' : 'Enhance Prompt'}
               </button>
            </div>
            <textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder="Describe the jewelry piece you want to create... e.g. A gold ring with a large sapphire surrounded by diamonds in an art deco style."
              className="w-full h-40 p-4 bg-stone-50 border border-stone-200 rounded-xl focus:ring-2 focus:ring-stone-900 focus:outline-none resize-none mb-6 placeholder:text-stone-400 text-stone-800 leading-relaxed"
            />

            {model === ImageModel.NANO_BANANA_PRO && (
                <>
                    <label className="text-xs font-bold text-stone-400 uppercase tracking-wider mb-3 block">Resolution (Pro Only)</label>
                    <div className="grid grid-cols-3 gap-3 mb-8">
                    {[ImageSize.SIZE_1K, ImageSize.SIZE_2K, ImageSize.SIZE_4K].map((s) => (
                        <button
                        key={s}
                        onClick={() => setSize(s)}
                        className={`py-2.5 px-4 rounded-xl text-sm font-medium transition-all ${
                            size === s 
                            ? 'bg-stone-900 text-white shadow-lg shadow-stone-900/20 scale-105' 
                            : 'bg-stone-50 text-stone-600 border border-stone-200 hover:bg-stone-100'
                        }`}
                        >
                        {s}
                        </button>
                    ))}
                    </div>
                </>
            )}

            <Button 
              onClick={handleGenerate} 
              disabled={!prompt || isGenerating}
              isLoading={isGenerating}
              className="w-full shadow-lg shadow-stone-900/20"
              size="lg"
            >
              <Zap className="w-5 h-5 mr-2 fill-current" />
              Generate Concept
            </Button>
            
            <div className="mt-6 p-4 bg-blue-50 text-blue-900 text-xs rounded-xl border border-blue-100 leading-relaxed">
               <p className="flex items-start gap-2">
                   <ExternalLink className="w-4 h-4 mt-0.5 flex-shrink-0 text-blue-500" />
                   <span>Nano Banana Pro requires a paid API key. <a href="https://ai.google.dev/gemini-api/docs/billing" target="_blank" rel="noreferrer" className="underline font-bold hover:text-blue-700">Billing Docs</a></span>
               </p>
            </div>
          </div>
        </div>

        <div className="lg:col-span-8">
           <div className="bg-stone-900 rounded-3xl min-h-[600px] flex items-center justify-center p-2 shadow-2xl shadow-stone-900/20 overflow-hidden relative">
              {/* Subtle grid pattern */}
              <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.03)_1px,transparent_1px)] bg-[size:40px_40px]"></div>

              {result ? (
                <div className="relative w-full h-full flex flex-col z-10">
                     <div className="flex-1 relative p-8 flex items-center justify-center">
                        <img src={result} alt="Generated Concept" className="max-w-full max-h-[650px] object-contain rounded-lg shadow-2xl ring-1 ring-white/10" />
                        <div className="absolute top-8 right-8 flex gap-2">
                            {model === ImageModel.NANO_BANANA_PRO && (
                                <div className="bg-black/40 backdrop-blur-md text-white px-3 py-1 rounded-full text-xs font-bold border border-white/10">
                                    {size}
                                </div>
                            )}
                            <div className="bg-black/40 backdrop-blur-md text-white px-3 py-1 rounded-full text-xs font-bold border border-white/10 flex items-center gap-1">
                                {model === ImageModel.NANO_BANANA_PRO ? 'Nano Banana Pro' : 'Nano Banana'}
                            </div>
                        </div>
                     </div>
                     <div className="p-6 bg-white/5 backdrop-blur-md border-t border-white/5 flex justify-end">
                       <Button variant="white" size="sm" onClick={() => onSave(result, prompt)}>
                         <Check className="w-4 h-4 mr-2" /> Save to Catalog
                       </Button>
                     </div>
                </div>
              ) : (
                <div className="text-stone-600 flex flex-col items-center relative z-10">
                    <div className="w-24 h-24 rounded-full border border-stone-800 flex items-center justify-center mb-6 animate-pulse">
                        <Sparkles className="w-10 h-10 text-stone-700" />
                    </div>
                    <p className="text-lg font-medium text-stone-400">Enter a prompt to imagine new designs</p>
                    <p className="text-sm text-stone-600 mt-2">Powered by {model === ImageModel.NANO_BANANA_PRO ? 'Nano Banana Pro' : 'Nano Banana'}</p>
                </div>
              )}
           </div>
        </div>
      </div>
    </div>
  );
};

export default Generator;